import React, { useEffect, useState } from 'react';
import { Save, Globe } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const WebPricingSection = ({ onShowToast }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [serviceFeePercent, setServiceFeePercent] = useState('1.8');
    const [commissionCard, setCommissionCard] = useState('');
    const [commissionSbp, setCommissionSbp] = useState('');
    const [webPriceFreekassa, setWebPriceFreekassa] = useState('');
    const [webPriceWata, setWebPriceWata] = useState('');
    const [webPriceLiontech, setWebPriceLiontech] = useState('');
    const [webPricePlatega, setWebPricePlatega] = useState('');
    const [webPricePear, setWebPricePear] = useState('');
    const [webPriceAurapay, setWebPriceAurapay] = useState('');
    const [webPriceLavepay, setWebPriceLavepay] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE_URL}/api/settings/web-pricing`, {
                    credentials: 'include',
                });
                if (!res.ok) throw new Error('load failed');
                const data = await res.json();
                setServiceFeePercent(String(data.service_fee_percent ?? '1.8'));
                setCommissionCard(String(data.commission_card ?? ''));
                setCommissionSbp(String(data.commission_sbp ?? ''));
                setWebPriceFreekassa(String(data.web_star_price_freekassa ?? ''));
                setWebPriceWata(String(data.web_star_price_wata ?? ''));
                setWebPriceLiontech(String(data.web_star_price_liontech ?? ''));
                setWebPricePlatega(String(data.web_star_price_platega ?? ''));
                setWebPricePear(String(data.web_star_price_pear ?? ''));
                setWebPriceAurapay(String(data.web_star_price_aurapay ?? ''));
                setWebPriceLavepay(String(data.web_star_price_lavepay ?? ''));
            } catch (e) {
                console.error(e);
                onShowToast?.('Не удалось загрузить цены сайта');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [onShowToast]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = {
                service_fee_percent: Number(serviceFeePercent),
                commission_card: commissionCard ? Number(commissionCard) : undefined,
                commission_sbp: commissionSbp ? Number(commissionSbp) : undefined,
                web_star_price_freekassa: webPriceFreekassa ? Number(webPriceFreekassa) : undefined,
                web_star_price_wata: webPriceWata ? Number(webPriceWata) : undefined,
                web_star_price_liontech: webPriceLiontech ? Number(webPriceLiontech) : undefined,
                web_star_price_platega: webPricePlatega ? Number(webPricePlatega) : undefined,
                web_star_price_pear: webPricePear ? Number(webPricePear) : undefined,
                web_star_price_aurapay: webPriceAurapay ? Number(webPriceAurapay) : undefined,
                web_star_price_lavepay: webPriceLavepay ? Number(webPriceLavepay) : undefined,
            };
            const res = await fetch(`${API_BASE_URL}/api/settings/web-pricing`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('save failed');
            onShowToast?.('Цены сайта сохранены');
        } catch (e) {
            console.error(e);
            onShowToast?.('Ошибка сохранения цен сайта');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-slate-500 py-4">Загрузка цен сайта...</div>;
    }

    return (
        <section className="mt-8 bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
                <Globe className="text-indigo-600" size={20} />
                <h2 className="text-lg font-semibold text-slate-900">Цены для веб-сайта</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">
                Отдельные цены за Star для checkout на сайте. Если поле пустое — используется цена бота для этого провайдера.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <label className="block">
                    <span className="text-sm text-slate-600">Сбор сервиса на сайте, %</span>
                    <input className="mt-1 w-full border rounded-lg px-3 py-2" value={serviceFeePercent} onChange={(e) => setServiceFeePercent(e.target.value)} />
                </label>
                <label className="block">
                    <span className="text-sm text-slate-600">Комиссия карта (сайт), %</span>
                    <input className="mt-1 w-full border rounded-lg px-3 py-2" value={commissionCard} onChange={(e) => setCommissionCard(e.target.value)} placeholder="как у бота" />
                </label>
                <label className="block">
                    <span className="text-sm text-slate-600">Комиссия СБП (сайт), %</span>
                    <input className="mt-1 w-full border rounded-lg px-3 py-2" value={commissionSbp} onChange={(e) => setCommissionSbp(e.target.value)} placeholder="как у бота" />
                </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                    ['FreeKassa', webPriceFreekassa, setWebPriceFreekassa],
                    ['Wata', webPriceWata, setWebPriceWata],
                    ['LionTech', webPriceLiontech, setWebPriceLiontech],
                    ['Platega', webPricePlatega, setWebPricePlatega],
                    ['Pear', webPricePear, setWebPricePear],
                    ['Aurapay', webPriceAurapay, setWebPriceAurapay],
                    ['Lave Pay', webPriceLavepay, setWebPriceLavepay],
                ].map(([label, value, setter]) => (
                    <label key={label} className="block">
                        <span className="text-sm text-slate-600">{label}, ₽/Star</span>
                        <input className="mt-1 w-full border rounded-lg px-3 py-2" value={value} onChange={(e) => setter(e.target.value)} placeholder="как у бота" />
                    </label>
                ))}
            </div>
            <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg disabled:opacity-50">
                <Save size={16} />
                {saving ? 'Сохранение...' : 'Сохранить цены сайта'}
            </button>
        </section>
    );
};

export default WebPricingSection;
